/**
 * Preset Voice Creator
 *
 * Admin interface for creating preset voices from YouTube audio:
 * 1. Extract audio from YouTube URL
 * 2. Preview and trim audio
 * 3. Configure voice profile (name, category, unlock requirements)
 * 4. Create voice via XTTS v2
 * 5. Test with sample phrases
 * 6. Enable/disable for users
 *
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Textarea } from '../../ui/textarea';
import {
  Youtube,
  Mic,
  Play,
  Pause,
  Volume2,
  Loader2,
  CheckCircle,
  XCircle,
  Plus,
  Trash2,
  RefreshCw,
  Download,
  Eye,
  EyeOff,
  Star,
  Lock,
  Unlock,
  Sparkles,
  AlertTriangle,
  Copy,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { auth } from '../../../utils/firebase/client';
import {
  extractYouTubeAudio,
  createPresetVoice,
  testPresetVoice,
  getPresetVoices,
  togglePresetVoiceEnabled,
  togglePresetVoiceNew,
  deletePresetVoice,
  VOICE_CATEGORIES,
  UNLOCK_TYPES,
  UNLOCK_REQUIREMENT_TYPES,
  SAMPLE_NAVIGATION_PHRASES,
  type PresetVoice,
  type VoiceCategory,
  type UnlockType,
  type UnlockRequirementType
} from '../../../utils/services/presetVoiceAdminService';

interface PresetVoiceCreatorProps {
  embedded?: boolean;
}

type ViewMode = 'list' | 'create';

export default function PresetVoiceCreator({ embedded = false }: PresetVoiceCreatorProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [voices, setVoices] = useState<PresetVoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const adminId = auth.currentUser?.uid || 'unknown';

  // Load voices
  const loadVoices = async () => {
    setLoading(true);
    try {
      const data = await getPresetVoices();
      setVoices(data);
    } catch (error: any) {
      toast.error('Failed to load preset voices');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVoices();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadVoices();
    setRefreshing(false);
  };

  const handleVoiceCreated = () => {
    setViewMode('list');
    loadVoices();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'p-6'}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Preset Voices</h2>
          <p className="text-sm text-gray-500">Create voice personas from YouTube for NavigatorX users</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {viewMode === 'list' ? (
            <Button onClick={() => setViewMode('create')}>
              <Plus className="h-4 w-4 mr-2" />
              Create Voice
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setViewMode('list')}>
              Back to List
            </Button>
          )}
        </div>
      </div>

      {viewMode === 'create' ? (
        <CreateVoiceForm adminId={adminId} onSuccess={handleVoiceCreated} onCancel={() => setViewMode('list')} />
      ) : (
        <VoicesList voices={voices} adminId={adminId} onUpdate={loadVoices} />
      )}
    </div>
  );
}

// =============================================================================
// VOICES LIST
// =============================================================================

interface VoicesListProps {
  voices: PresetVoice[];
  adminId: string;
  onUpdate: () => void;
}

function VoicesList({ voices, adminId, onUpdate }: VoicesListProps) {
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [testingVoiceId, setTestingVoiceId] = useState<string | null>(null);
  const [testAudio, setTestAudio] = useState<HTMLAudioElement | null>(null);

  const handleToggleEnabled = async (voice: PresetVoice) => {
    setProcessingIds(prev => new Set([...prev, voice.id]));
    try {
      await togglePresetVoiceEnabled(voice.id, !voice.isEnabled);
      toast.success(voice.isEnabled ? 'Voice disabled' : 'Voice enabled');
      onUpdate();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(voice.id);
        return next;
      });
    }
  };

  const handleToggleNew = async (voice: PresetVoice) => {
    setProcessingIds(prev => new Set([...prev, voice.id]));
    try {
      await togglePresetVoiceNew(voice.id, !voice.isNew);
      toast.success(voice.isNew ? 'NEW badge removed' : 'NEW badge added');
      onUpdate();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(voice.id);
        return next;
      });
    }
  };

  const handleDelete = async (voice: PresetVoice) => {
    if (!confirm(`Delete "${voice.name}"? This cannot be undone.`)) return;

    setProcessingIds(prev => new Set([...prev, voice.id]));
    try {
      await deletePresetVoice(voice.id, adminId);
      toast.success('Voice deleted');
      onUpdate();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(voice.id);
        return next;
      });
    }
  };

  const handleTestVoice = async (voice: PresetVoice) => {
    if (testAudio) {
      testAudio.pause();
      setTestAudio(null);
    }

    setTestingVoiceId(voice.id);
    try {
      const result = await testPresetVoice({
        voiceId: voice.id,
        text: SAMPLE_NAVIGATION_PHRASES[Math.floor(Math.random() * SAMPLE_NAVIGATION_PHRASES.length)]
      });

      const audio = new Audio(`data:audio/wav;base64,${result.audioBase64}`);
      audio.onended = () => {
        setTestingVoiceId(null);
        setTestAudio(null);
      };
      audio.play();
      setTestAudio(audio);
    } catch (error: any) {
      toast.error(error.message);
      setTestingVoiceId(null);
    }
  };

  const getCategoryColor = (category: string): string => {
    switch (category) {
      case 'PROFESSIONAL': return 'bg-blue-100 text-blue-700';
      case 'REGIONAL': return 'bg-green-100 text-green-700';
      case 'CHARACTER': return 'bg-purple-100 text-purple-700';
      case 'LEGENDS': return 'bg-yellow-100 text-yellow-700';
      case 'ADMIN_PUBLIC': return 'bg-violet-100 text-violet-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getUnlockBadge = (voice: PresetVoice) => {
    switch (voice.unlockType) {
      case 'FREE':
        return <Badge className="bg-green-100 text-green-700">Free</Badge>;
      case 'PRO_ONLY':
        return <Badge className="bg-amber-100 text-amber-700">Pro Only</Badge>;
      case 'ACHIEVEMENT':
        return (
          <Badge className="bg-yellow-100 text-yellow-700">
            {voice.unlockRequirement?.type.replace('_', ' ')} {voice.unlockRequirement?.value}
          </Badge>
        );
      default:
        return null;
    }
  };

  if (voices.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Mic className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No preset voices yet</p>
          <p className="text-sm text-gray-400">Create your first voice from a YouTube video</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-gray-500">Total</p>
            <p className="text-2xl font-bold">{voices.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-gray-500">Enabled</p>
            <p className="text-2xl font-bold text-green-600">{voices.filter(v => v.isEnabled).length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-gray-500">Free</p>
            <p className="text-2xl font-bold">{voices.filter(v => v.unlockType === 'FREE').length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-gray-500">Avg Quality</p>
            <p className="text-2xl font-bold">
              {voices.length > 0
                ? `${Math.round((voices.reduce((sum, v) => sum + (v.qualityScore || 0), 0) / voices.length) * 100)}%`
                : '-'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* List */}
      {voices.map(voice => {
        const isProcessing = processingIds.has(voice.id);
        const isTesting = testingVoiceId === voice.id;

        return (
          <Card key={voice.id} className={!voice.isEnabled ? 'opacity-60' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                {/* Icon */}
                <div className={`p-3 rounded-full ${voice.isEnabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
                  {voice.iconEmoji ? (
                    <span className="text-2xl">{voice.iconEmoji}</span>
                  ) : (
                    <Mic className={`h-6 w-6 ${voice.isEnabled ? 'text-blue-600' : 'text-gray-400'}`} />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">{voice.name}</span>
                    <Badge className={getCategoryColor(voice.category)}>{voice.category}</Badge>
                    {getUnlockBadge(voice)}
                    {voice.isNew && <Badge className="bg-emerald-100 text-emerald-700">NEW</Badge>}
                    {voice.isHighQuality && <Badge className="bg-purple-100 text-purple-700">HQ</Badge>}
                  </div>
                  <p className="text-sm text-gray-500 truncate">{voice.description}</p>
                  <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                    <span>Quality: {voice.qualityScore ? `${Math.round(voice.qualityScore * 100)}%` : 'N/A'}</span>
                    {voice.sourceUrl && (
                      <a
                        href={voice.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-blue-500 hover:underline"
                      >
                        <Youtube className="h-3 w-3" />
                        Source
                      </a>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {/* Test */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestVoice(voice)}
                    disabled={isTesting}
                  >
                    {isTesting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Volume2 className="h-4 w-4" />
                    )}
                  </Button>

                  {/* Toggle NEW */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleNew(voice)}
                    disabled={isProcessing}
                    title={voice.isNew ? 'Remove NEW badge' : 'Add NEW badge'}
                  >
                    <Sparkles className={`h-4 w-4 ${voice.isNew ? 'text-emerald-500' : 'text-gray-400'}`} />
                  </Button>

                  {/* Toggle Enabled */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleEnabled(voice)}
                    disabled={isProcessing}
                    className={voice.isEnabled ? 'text-green-600' : 'text-gray-400'}
                  >
                    {isProcessing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : voice.isEnabled ? (
                      <Eye className="h-4 w-4" />
                    ) : (
                      <EyeOff className="h-4 w-4" />
                    )}
                  </Button>

                  {/* Delete */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(voice)}
                    disabled={isProcessing}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// =============================================================================
// CREATE VOICE FORM
// =============================================================================

interface CreateVoiceFormProps {
  adminId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

function CreateVoiceForm({ adminId, onSuccess, onCancel }: CreateVoiceFormProps) {
  // Step state
  const [step, setStep] = useState<'extract' | 'configure' | 'create' | 'test'>('extract');

  // Extract step
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [startTime, setStartTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(60);
  const [extracting, setExtracting] = useState(false);
  const [extractedAudio, setExtractedAudio] = useState<{ path: string; url: string; duration: number } | null>(null);

  // Configure step
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<VoiceCategory>('PROFESSIONAL');
  const [unlockType, setUnlockType] = useState<UnlockType>('FREE');
  const [unlockRequirementType, setUnlockRequirementType] = useState<UnlockRequirementType>('MILES_DRIVEN');
  const [unlockRequirementValue, setUnlockRequirementValue] = useState<number>(100);
  const [iconEmoji, setIconEmoji] = useState('');

  // Create step
  const [creating, setCreating] = useState(false);
  const [createdVoice, setCreatedVoice] = useState<{ voiceId: string; qualityScore: number } | null>(null);

  // Test step
  const [testText, setTestText] = useState(SAMPLE_NAVIGATION_PHRASES[0]);
  const [testing, setTesting] = useState(false);
  const [testAudioUrl, setTestAudioUrl] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement>(null);

  // Extract YouTube audio
  const handleExtract = async () => {
    if (!youtubeUrl) {
      toast.error('Please enter a YouTube URL');
      return;
    }

    setExtracting(true);
    try {
      const result = await extractYouTubeAudio({
        youtubeUrl,
        startTime: startTime > 0 ? startTime : undefined,
        duration
      });

      setExtractedAudio({
        path: result.audioPath,
        url: result.signedUrl,
        duration: result.durationSeconds
      });

      toast.success('Audio extracted successfully');
      setStep('configure');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setExtracting(false);
    }
  };

  // Create voice
  const handleCreate = async () => {
    if (!extractedAudio) return;
    if (!name.trim()) {
      toast.error('Please enter a voice name');
      return;
    }

    setCreating(true);
    setStep('create');

    try {
      const result = await createPresetVoice({
        name,
        description,
        category,
        audioPath: extractedAudio.path,
        sourceUrl: youtubeUrl,
        unlockType,
        unlockRequirement: unlockType === 'ACHIEVEMENT' ? {
          type: unlockRequirementType,
          value: unlockRequirementValue
        } : undefined,
        iconEmoji: iconEmoji || undefined,
        adminId
      });

      setCreatedVoice({
        voiceId: result.voiceId,
        qualityScore: result.qualityScore
      });

      toast.success('Voice created successfully');
      setStep('test');
    } catch (error: any) {
      toast.error(error.message);
      setStep('configure');
    } finally {
      setCreating(false);
    }
  };

  // Test voice
  const handleTest = async () => {
    if (!createdVoice) return;

    setTesting(true);
    try {
      const result = await testPresetVoice({
        voiceId: createdVoice.voiceId,
        text: testText
      });

      setTestAudioUrl(`data:audio/wav;base64,${result.audioBase64}`);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Progress */}
      <div className="flex items-center justify-between mb-8">
        {['extract', 'configure', 'create', 'test'].map((s, i) => (
          <div key={s} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === s
                  ? 'bg-blue-500 text-white'
                  : ['extract', 'configure', 'create', 'test'].indexOf(step) > i
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {['extract', 'configure', 'create', 'test'].indexOf(step) > i ? (
                <CheckCircle className="h-5 w-5" />
              ) : (
                i + 1
              )}
            </div>
            {i < 3 && <div className="w-16 h-1 bg-gray-200 mx-2" />}
          </div>
        ))}
      </div>

      {/* Step: Extract */}
      {step === 'extract' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Youtube className="h-5 w-5 text-red-500" />
              Extract Audio from YouTube
            </CardTitle>
            <CardDescription>
              Paste a YouTube URL to extract audio for voice cloning
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>YouTube URL</Label>
              <Input
                placeholder="https://www.youtube.com/watch?v=..."
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time (seconds)</Label>
                <Input
                  type="number"
                  min={0}
                  value={startTime}
                  onChange={(e) => setStartTime(Number(e.target.value))}
                />
              </div>
              <div>
                <Label>Duration (seconds)</Label>
                <Input
                  type="number"
                  min={10}
                  max={300}
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                />
                <p className="text-xs text-gray-500 mt-1">Recommended: 30-60 seconds</p>
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button onClick={handleExtract} disabled={extracting || !youtubeUrl}>
                {extracting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Extracting...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Extract Audio
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step: Configure */}
      {step === 'configure' && extractedAudio && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mic className="h-5 w-5 text-blue-500" />
              Configure Voice Profile
            </CardTitle>
            <CardDescription>
              Set up how this voice will appear to NavigatorX users
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Audio Preview */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <Label className="mb-2 block">Extracted Audio ({extractedAudio.duration.toFixed(1)}s)</Label>
              <audio ref={audioRef} src={extractedAudio.url} controls className="w-full" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Voice Name *</Label>
                <Input
                  placeholder="e.g., Texas Trucker Tom"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <Label>Icon Emoji</Label>
                <Input
                  placeholder="e.g., cowboy"
                  value={iconEmoji}
                  onChange={(e) => setIconEmoji(e.target.value)}
                  maxLength={2}
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="Brief description of the voice..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <select
                  className="w-full border rounded-md p-2"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as VoiceCategory)}
                >
                  {VOICE_CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Unlock Type</Label>
                <select
                  className="w-full border rounded-md p-2"
                  value={unlockType}
                  onChange={(e) => setUnlockType(e.target.value as UnlockType)}
                >
                  {UNLOCK_TYPES.map(u => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {unlockType === 'ACHIEVEMENT' && (
              <div className="grid grid-cols-2 gap-4 p-4 bg-yellow-50 rounded-lg">
                <div>
                  <Label>Requirement Type</Label>
                  <select
                    className="w-full border rounded-md p-2"
                    value={unlockRequirementType}
                    onChange={(e) => setUnlockRequirementType(e.target.value as UnlockRequirementType)}
                  >
                    {UNLOCK_REQUIREMENT_TYPES.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Required Value</Label>
                  <Input
                    type="number"
                    min={1}
                    value={unlockRequirementValue}
                    onChange={(e) => setUnlockRequirementValue(Number(e.target.value))}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {UNLOCK_REQUIREMENT_TYPES.find(r => r.value === unlockRequirementType)?.unit}
                  </p>
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setStep('extract')}>
                Back
              </Button>
              <Button onClick={handleCreate} disabled={!name.trim()}>
                Create Voice
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step: Creating */}
      {step === 'create' && creating && (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
            <p className="text-lg font-medium">Creating Voice...</p>
            <p className="text-sm text-gray-500 mt-2">
              Processing audio with XTTS v2. This may take a moment.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Step: Test */}
      {step === 'test' && createdVoice && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Voice Created Successfully
            </CardTitle>
            <CardDescription>
              Quality Score: {Math.round(createdVoice.qualityScore * 100)}%
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Test with a phrase</Label>
              <select
                className="w-full border rounded-md p-2"
                value={testText}
                onChange={(e) => setTestText(e.target.value)}
              >
                {SAMPLE_NAVIGATION_PHRASES.map((phrase, i) => (
                  <option key={i} value={phrase}>{phrase}</option>
                ))}
              </select>
            </div>

            <Button onClick={handleTest} disabled={testing} className="w-full">
              {testing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Volume2 className="h-4 w-4 mr-2" />
                  Test Voice
                </>
              )}
            </Button>

            {testAudioUrl && (
              <audio src={testAudioUrl} controls autoPlay className="w-full" />
            )}

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={onSuccess} className="flex-1">
                Done
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
